"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import PageHero from "@/components/page-hero";
import { pageHeroData } from "@/data/page-hero-data";
import {
  Star,
  ChevronDown,
  Users,
  Clock,
  Calendar,
  Tag,
  MapPin,
  Quote,
  Loader2,
  MessageSquare,
  X,
  SlidersHorizontal,
} from "lucide-react";

const API = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api/v1";

// ── Types ──

export interface PublicReview {
  _id: string;
  displayName: string;
  trackName?: string;
  packageName?: string;
  rideDate?: string;
  slotCount?: number;
  rideDurationMin?: number;
  seatCount?: number;
  rating: number;
  comment?: string;
  ratingFun?: number;
  ratingService?: number;
  ratingCleanliness?: number;
  ratingValue?: number;
  completedAt: string;
}

export interface PublicStats {
  totalReviews: number;
  averageRating: number;
  categoryAverages: {
    fun: number;
    service: number;
    cleanliness: number;
    value: number;
  };
  starDistribution: Record<number, number>;
  packages?: string[];
  tracks?: string[];
}

// ── Filter-Typen ──

type SortKey = "newest" | "best" | "worst" | "longest";
type SeatGroup = "single" | "duo" | "small" | "medium" | "large";
type TopCategory = "fun" | "service" | "cleanliness" | "value";
type Period = "3m" | "12m";

interface Filters {
  rating?: number;
  hasComment?: boolean;
  packageName?: string;
  trackName?: string;
  seatGroup?: SeatGroup;
  topCategory?: TopCategory;
  period?: Period;
}

const SEAT_GROUP_LABELS: Record<SeatGroup, string> = {
  single: "Einzelfahrer",
  duo: "2 Personen",
  small: "Kleingruppe (3–4)",
  medium: "Gruppe (5–8)",
  large: "Große Gruppe (9+)",
};
const TOP_CATEGORY_LABELS: Record<TopCategory, string> = {
  fun: "Top: Spaß",
  service: "Top: Service",
  cleanliness: "Top: Sauberkeit",
  value: "Top: Preis-Leistung",
};
const PERIOD_LABELS: Record<Period, string> = {
  "3m": "Letzte 3 Monate",
  "12m": "Letzte 12 Monate",
};
const SORT_LABELS: Record<SortKey, string> = {
  newest: "Neueste zuerst",
  best: "Beste zuerst",
  worst: "Kritischste zuerst",
  longest: "Ausführlichste zuerst",
};

interface BewertungenClientProps {
  /** Serverseitig vorgeladene erste Seite (für SEO/JSON-LD ist das HTML dann nicht leer). */
  initialReviews?: PublicReview[];
  initialStats?: PublicStats | null;
  initialTotal?: number;
  initialTotalPages?: number;
}

// ── Helpers ──

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("de-DE", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "Europe/Berlin",
  });
}

function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "Europe/Berlin",
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Berlin",
  });
}

/** Baut die Query-String für die Bewertungs-Liste aus Seite, Filtern und Sortierung. */
function buildListQuery(page: number, filters: Filters, sort: SortKey): string {
  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("limit", "12");
  if (filters.rating) params.set("rating", String(filters.rating));
  if (filters.hasComment) params.set("hasComment", "true");
  if (filters.packageName) params.set("packageName", filters.packageName);
  if (filters.trackName) params.set("trackName", filters.trackName);
  if (filters.seatGroup) params.set("seatGroup", filters.seatGroup);
  if (filters.topCategory) params.set("topCategory", filters.topCategory);
  if (filters.period) params.set("period", filters.period);
  if (sort && sort !== "newest") params.set("sort", sort);
  return params.toString();
}

// ── Stars Component ──

function Stars({ rating, size = 16, className = "" }: { rating: number; size?: number; className?: string }) {
  return (
    <div className={`flex gap-0.5 ${className}`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          className={i <= rating ? "fill-amber-400 text-amber-400" : "text-gray-200"}
        />
      ))}
    </div>
  );
}

function StarsSmooth({ rating, size = 14 }: { rating: number; size?: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => {
        const fill = Math.min(1, Math.max(0, rating - (i - 1)));
        return (
          <div key={i} className="relative" style={{ width: size, height: size }}>
            <Star size={size} className="text-gray-200 absolute inset-0" />
            <div className="absolute inset-0 overflow-hidden" style={{ width: `${fill * 100}%` }}>
              <Star size={size} className="fill-amber-400 text-amber-400" />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Category Rating Bar ──

function CategoryBar({ label, value, icon }: { label: string; value: number; icon: string }) {
  const pct = (value / 5) * 100;
  return (
    <div className="flex items-center gap-3">
      <span className="text-lg">{icon}</span>
      <div className="flex-1">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-gray-700">{label}</span>
          <span className="text-sm font-bold text-gray-900">{value > 0 ? value.toFixed(1) : "–"}</span>
        </div>
        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-500 transition-all duration-700"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    </div>
  );
}

// ── Star Distribution Bar (klickbar → filtert auf diese Sterne-Zahl) ──

function DistributionBar({
  star,
  count,
  total,
  active,
  onClick,
}: {
  star: number;
  count: number;
  total: number;
  active?: boolean;
  onClick?: () => void;
}) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={`Nur ${star}-Sterne-Bewertungen anzeigen`}
      className={`flex w-full items-center gap-2 rounded-lg px-2 py-0.5 -mx-2 transition-colors ${
        active ? "bg-amber-50 ring-1 ring-amber-200" : "hover:bg-gray-50"
      }`}
    >
      <span className={`text-sm font-medium w-6 text-right ${active ? "text-amber-600" : "text-gray-500"}`}>{star}</span>
      <Star size={12} className="fill-amber-400 text-amber-400 shrink-0" />
      <div className="flex-1 h-2.5 rounded-full bg-gray-100 overflow-hidden">
        <div
          className="h-full rounded-full bg-amber-400 transition-all duration-700"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-gray-400 w-8 text-right">{count}</span>
    </button>
  );
}

// ── Filter-Dropdown (styled native select) ──

function FilterDropdown({
  value,
  onChange,
  allLabel,
  options,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  allLabel: string;
  options: { value: string; label: string }[];
  ariaLabel: string;
}) {
  const active = value !== "";
  return (
    <div className="relative w-full sm:w-auto">
      <select
        aria-label={ariaLabel}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full sm:w-auto appearance-none rounded-full border py-2 pl-4 pr-9 text-sm font-medium cursor-pointer focus:outline-none focus:border-[var(--main-color)] transition-colors ${
          active
            ? "border-[var(--main-color)] bg-amber-50 text-gray-900"
            : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
        }`}
      >
        <option value="">{allLabel}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown
        size={15}
        className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 ${active ? "text-[var(--main-color)]" : "text-gray-400"}`}
      />
    </div>
  );
}

// ── Aktiver-Filter-Chip ──

function ActiveChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--main-color)]/10 border border-[var(--main-color)]/20 px-3 py-1 text-xs font-semibold text-[var(--main-color)]">
      {label}
      <button type="button" onClick={onRemove} aria-label={`Filter „${label}“ entfernen`} className="hover:opacity-70">
        <X size={13} />
      </button>
    </span>
  );
}

// ── Review Card ──

function ReviewCard({ review }: { review: PublicReview }) {
  const [expanded, setExpanded] = useState(false);
  const hasSubRatings = review.ratingFun || review.ratingService || review.ratingCleanliness || review.ratingValue;
  const hasBookingDetails = review.trackName || review.packageName || review.rideDate || review.slotCount;
  const commentLong = (review.comment?.length ?? 0) > 200;

  return (
    <div className="group relative rounded-2xl border border-gray-100 bg-white p-6 shadow-sm hover:shadow-md transition-all duration-300">
      {/* Top: Avatar + Name + Rating + Date */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          {/* Avatar Circle */}
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[var(--main-color)] to-amber-500 flex items-center justify-center text-white font-bold text-base shrink-0 shadow-sm">
            {review.displayName.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="font-semibold text-gray-900">{review.displayName}</p>
            <p className="text-xs text-gray-400">
              {formatDate(review.completedAt)}
            </p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <Stars rating={review.rating} size={18} />
          <p className="text-xs text-gray-400 mt-0.5 font-medium">{review.rating},0 / 5</p>
        </div>
      </div>

      {/* Booking Details Chips */}
      {hasBookingDetails && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {review.trackName && (
            <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-600 border border-gray-100">
              <MapPin size={11} className="text-[var(--main-color)]" />
              {review.trackName}
            </span>
          )}
          {review.packageName && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 border border-amber-100">
              <Tag size={11} />
              {review.packageName}
            </span>
          )}
          {review.rideDate && (
            <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-600 border border-gray-100">
              <Calendar size={11} className="text-gray-400" />
              {formatShortDate(review.rideDate)}{" "}
              <span className="text-gray-400">·</span>{" "}
              {formatTime(review.rideDate)} Uhr
            </span>
          )}
          {/* „N Slots · M Min." nur für Einzelfahrten – bei Paketen ist die Summe über
              Heats/Sessions irreführend (das Paket-Label beschreibt das Erlebnis bereits). */}
          {!review.packageName && review.slotCount && review.slotCount > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-600 border border-gray-100">
              <Clock size={11} className="text-gray-400" />
              {review.slotCount} {review.slotCount === 1 ? "Slot" : "Slots"}
              {review.rideDurationMin ? ` · ${review.rideDurationMin} Min.` : ""}
            </span>
          )}
          {review.seatCount && review.seatCount > 1 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-600 border border-gray-100">
              <Users size={11} className="text-gray-400" />
              {review.seatCount} Personen
            </span>
          )}
        </div>
      )}

      {/* Comment */}
      {review.comment && (
        <div className="mb-4">
          <div className="relative">
            <Quote size={16} className="text-gray-200 absolute -left-0.5 -top-1" />
            <p className={`text-gray-700 leading-relaxed pl-5 ${!expanded && commentLong ? "line-clamp-3" : ""}`}>
              {review.comment}
            </p>
          </div>
          {commentLong && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-[var(--main-color)] font-medium mt-1 pl-5 hover:underline"
            >
              {expanded ? "Weniger anzeigen" : "Weiterlesen"}
            </button>
          )}
        </div>
      )}

      {/* Sub-Ratings */}
      {hasSubRatings && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 pt-3 border-t border-gray-50">
          {review.ratingFun && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">🏎️ Spaß</span>
              <div className="flex items-center gap-1">
                <Stars rating={review.ratingFun} size={10} />
              </div>
            </div>
          )}
          {review.ratingService && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">👋 Service</span>
              <div className="flex items-center gap-1">
                <Stars rating={review.ratingService} size={10} />
              </div>
            </div>
          )}
          {review.ratingCleanliness && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">✨ Sauberkeit</span>
              <div className="flex items-center gap-1">
                <Stars rating={review.ratingCleanliness} size={10} />
              </div>
            </div>
          )}
          {review.ratingValue && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">💰 Preis-Leistung</span>
              <div className="flex items-center gap-1">
                <Stars rating={review.ratingValue} size={10} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════
//  Client (interaktiv: Filter, Sortierung, Load-More)
// ═══════════════════════════════════════════

export default function BewertungenClient({
  initialReviews,
  initialStats,
  initialTotal,
  initialTotalPages,
}: BewertungenClientProps) {
  const heroData = pageHeroData.bewertungen;
  const hasInitial = Array.isArray(initialReviews);

  const [reviews, setReviews] = useState<PublicReview[]>(initialReviews ?? []);
  const [stats, setStats] = useState<PublicStats | null>(initialStats ?? null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(initialTotalPages ?? 1);
  const [total, setTotal] = useState(initialTotal ?? 0);
  const [loading, setLoading] = useState(!hasInitial);
  const [loadingMore, setLoadingMore] = useState(false);

  // Filter- & Sortier-Status
  const [filters, setFilters] = useState<Filters>({});
  const [sort, setSort] = useState<SortKey>("newest");
  const [filtersOpen, setFiltersOpen] = useState(false); // nur relevant auf Mobile (Panel ein-/ausklappen)

  const activeFilterCount = [
    filters.rating !== undefined,
    filters.hasComment,
    filters.packageName,
    filters.trackName,
    filters.seatGroup,
    filters.topCategory,
    filters.period,
  ].filter(Boolean).length;
  const hasActiveFilters = activeFilterCount > 0;

  const loadStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/reviews/public/stats`);
      if (res.ok) {
        const json = await res.json();
        setStats(json.data ?? json);
      }
    } catch { /* ignore */ }
  }, []);

  const loadReviews = useCallback(
    async (p: number, append: boolean, f: Filters, s: SortKey) => {
      if (append) setLoadingMore(true); else setLoading(true);
      try {
        const res = await fetch(`${API}/reviews/public/list?${buildListQuery(p, f, s)}`);
        if (res.ok) {
          const json = await res.json();
          const payload = json.data ?? json;
          const items = Array.isArray(payload.data) ? payload.data : Array.isArray(payload) ? payload : [];
          setReviews((prev) => (append ? [...prev, ...items] : items));
          setTotalPages(payload.totalPages || 1);
          setTotal(payload.total || 0);
          setPage(p);
        }
      } catch { /* ignore */ }
      setLoading(false);
      setLoadingMore(false);
    },
    [],
  );

  // Erst-Load nur, wenn nicht serverseitig vorgeladen (kein Flash, kein Doppel-Request).
  useEffect(() => {
    if (hasInitial) return;
    loadStats();
    loadReviews(1, false, {}, "newest");
  }, [hasInitial, loadStats, loadReviews]);

  // Bei Filter-/Sortier-Änderung immer ab Seite 1 neu laden (den ersten Render überspringen).
  const skipFirstFilterRun = useRef(true);
  useEffect(() => {
    if (skipFirstFilterRun.current) {
      skipFirstFilterRun.current = false;
      return;
    }
    loadReviews(1, false, filters, sort);
  }, [filters, sort, loadReviews]);

  const handleLoadMore = () => {
    if (page < totalPages) loadReviews(page + 1, true, filters, sort);
  };

  const updateFilter = (patch: Partial<Filters>) => setFilters((prev) => ({ ...prev, ...patch }));
  const clearFilters = () => setFilters({});

  const packages = stats?.packages ?? [];
  const tracks = stats?.tracks ?? [];

  return (
    <>
      <PageHero title={heroData.title} description={heroData.description} backgroundImage={heroData.backgroundImage} />

      {/* ── Stats Overview ── */}
      {stats && stats.totalReviews > 0 && (
        <section className="relative -mt-16 z-10 pb-8">
          <div className="container-custom max-w-5xl">
            <div className="rounded-2xl bg-white shadow-xl border border-gray-100 p-8 md:p-10">
              <div className="grid md:grid-cols-3 gap-8 md:gap-12">
                {/* Left: Overall Rating */}
                <div className="flex flex-col items-center justify-center text-center">
                  <p className="text-8xl font-black text-gray-900 leading-none">
                    {stats.averageRating.toFixed(1)}
                  </p>
                  <StarsSmooth rating={stats.averageRating} size={32} />
                  <p className="text-base text-gray-500 mt-3">
                    basierend auf <span className="font-semibold text-gray-700">{stats.totalReviews}</span>{" "}
                    {stats.totalReviews === 1 ? "Bewertung" : "Bewertungen"}
                  </p>
                </div>

                {/* Middle: Star Distribution (klickbar) */}
                <div className="space-y-1.5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Sterne-Verteilung</p>
                  {[5, 4, 3, 2, 1].map((s) => (
                    <DistributionBar
                      key={s}
                      star={s}
                      count={stats.starDistribution[s] || 0}
                      total={stats.totalReviews}
                      active={filters.rating === s}
                      onClick={() => updateFilter({ rating: filters.rating === s ? undefined : s })}
                    />
                  ))}
                </div>

                {/* Right: Category Averages */}
                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Kategorien</p>
                  <CategoryBar label="Spaßfaktor" value={stats.categoryAverages.fun} icon="🏎️" />
                  <CategoryBar label="Service" value={stats.categoryAverages.service} icon="👋" />
                  <CategoryBar label="Sauberkeit" value={stats.categoryAverages.cleanliness} icon="✨" />
                  <CategoryBar label="Preis-Leistung" value={stats.categoryAverages.value} icon="💰" />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Reviews Grid ── */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="container-custom max-w-5xl">
          {/* ── Filterleiste ── */}
          <div className="mb-6 rounded-2xl bg-white border border-gray-100 shadow-sm p-4 md:p-5">
            {/* Kopfzeile: Label + Mobile-Toggle */}
            <div className="flex items-center gap-2.5">
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500">
                <SlidersHorizontal size={15} className="text-[var(--main-color)]" />
                Filter
              </span>
              <button
                type="button"
                onClick={() => setFiltersOpen((o) => !o)}
                aria-expanded={filtersOpen}
                className="sm:hidden ml-auto inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700"
              >
                {activeFilterCount > 0 ? `${activeFilterCount} aktiv` : "Anzeigen"}
                <ChevronDown size={15} className={`transition-transform ${filtersOpen ? "rotate-180" : ""}`} />
              </button>
            </div>

            {/* Controls: Mobile ein-/ausklappbar, Desktop inline & immer sichtbar */}
            <div className={`${filtersOpen ? "grid" : "hidden"} sm:flex grid-cols-1 sm:flex-wrap sm:items-center gap-2.5 mt-3.5 sm:mt-3`}>
              {/* Nur mit Kommentar */}
              <button
                type="button"
                onClick={() => updateFilter({ hasComment: filters.hasComment ? undefined : true })}
                aria-pressed={!!filters.hasComment}
                className={`w-full sm:w-auto inline-flex items-center justify-center sm:justify-start gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                  filters.hasComment
                    ? "border-[var(--main-color)] bg-amber-50 text-gray-900"
                    : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                }`}
              >
                <MessageSquare size={14} />
                Nur mit Text
              </button>

              {/* Paket */}
              {packages.length > 0 && (
                <FilterDropdown
                  ariaLabel="Nach Paket filtern"
                  allLabel="Alle Pakete"
                  value={filters.packageName ?? ""}
                  onChange={(v) => updateFilter({ packageName: v || undefined })}
                  options={packages.map((p) => ({ value: p, label: p }))}
                />
              )}

              {/* Bahn (nur bei mehreren) */}
              {tracks.length > 1 && (
                <FilterDropdown
                  ariaLabel="Nach Bahn filtern"
                  allLabel="Alle Bahnen"
                  value={filters.trackName ?? ""}
                  onChange={(v) => updateFilter({ trackName: v || undefined })}
                  options={tracks.map((t) => ({ value: t, label: t }))}
                />
              )}

              {/* Gruppengröße */}
              <FilterDropdown
                ariaLabel="Nach Gruppengröße filtern"
                allLabel="Alle Gruppengrößen"
                value={filters.seatGroup ?? ""}
                onChange={(v) => updateFilter({ seatGroup: (v || undefined) as SeatGroup | undefined })}
                options={(Object.keys(SEAT_GROUP_LABELS) as SeatGroup[]).map((k) => ({
                  value: k,
                  label: SEAT_GROUP_LABELS[k],
                }))}
              />

              {/* Kategorie-Fokus */}
              <FilterDropdown
                ariaLabel="Nach Kategorie-Stärke filtern"
                allLabel="Alle Kategorien"
                value={filters.topCategory ?? ""}
                onChange={(v) => updateFilter({ topCategory: (v || undefined) as TopCategory | undefined })}
                options={(Object.keys(TOP_CATEGORY_LABELS) as TopCategory[]).map((k) => ({
                  value: k,
                  label: TOP_CATEGORY_LABELS[k],
                }))}
              />

              {/* Zeitraum */}
              <FilterDropdown
                ariaLabel="Nach Zeitraum filtern"
                allLabel="Gesamter Zeitraum"
                value={filters.period ?? ""}
                onChange={(v) => updateFilter({ period: (v || undefined) as Period | undefined })}
                options={(Object.keys(PERIOD_LABELS) as Period[]).map((k) => ({ value: k, label: PERIOD_LABELS[k] }))}
              />

              {/* Sortierung – Desktop rechts abgesetzt, Mobile volle Breite */}
              <div className="relative w-full sm:w-auto sm:ml-auto">
                <select
                  aria-label="Sortierung"
                  value={sort}
                  onChange={(e) => setSort(e.target.value as SortKey)}
                  className="w-full sm:w-auto appearance-none rounded-full border border-gray-200 bg-white py-2 pl-4 pr-9 text-sm font-medium text-gray-700 cursor-pointer hover:border-gray-300 focus:outline-none focus:border-[var(--main-color)]"
                >
                  {(Object.keys(SORT_LABELS) as SortKey[]).map((k) => (
                    <option key={k} value={k}>
                      {SORT_LABELS[k]}
                    </option>
                  ))}
                </select>
                <ChevronDown size={15} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            {/* Aktive Filter-Chips */}
            {hasActiveFilters && (
              <div className="flex flex-wrap items-center gap-2 mt-3.5 pt-3.5 border-t border-gray-100">
                {filters.rating !== undefined && (
                  <ActiveChip label={`${filters.rating} Sterne`} onRemove={() => updateFilter({ rating: undefined })} />
                )}
                {filters.hasComment && (
                  <ActiveChip label="Mit Text" onRemove={() => updateFilter({ hasComment: undefined })} />
                )}
                {filters.packageName && (
                  <ActiveChip label={filters.packageName} onRemove={() => updateFilter({ packageName: undefined })} />
                )}
                {filters.trackName && (
                  <ActiveChip label={filters.trackName} onRemove={() => updateFilter({ trackName: undefined })} />
                )}
                {filters.seatGroup && (
                  <ActiveChip
                    label={SEAT_GROUP_LABELS[filters.seatGroup]}
                    onRemove={() => updateFilter({ seatGroup: undefined })}
                  />
                )}
                {filters.topCategory && (
                  <ActiveChip
                    label={TOP_CATEGORY_LABELS[filters.topCategory]}
                    onRemove={() => updateFilter({ topCategory: undefined })}
                  />
                )}
                {filters.period && (
                  <ActiveChip label={PERIOD_LABELS[filters.period]} onRemove={() => updateFilter({ period: undefined })} />
                )}
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-xs font-semibold text-gray-500 hover:text-gray-800 underline underline-offset-2 ml-1"
                >
                  Alle zurücksetzen
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="h-10 w-10 animate-spin text-[var(--main-color)] mb-4" />
              <p className="text-gray-500">Bewertungen werden geladen…</p>
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-20">
              <Star size={48} className="mx-auto text-gray-200 mb-4" />
              {hasActiveFilters ? (
                <>
                  <h2 className="text-xl font-bold text-gray-700 mb-2">Keine passenden Bewertungen</h2>
                  <p className="text-gray-500 mb-5">
                    Für die gewählten Filter gibt es keine Bewertungen. Passe die Filter an oder setze sie zurück.
                  </p>
                  <button
                    onClick={clearFilters}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border-2 border-[var(--main-color)] text-[var(--main-color)] font-semibold hover:bg-[var(--main-color)] hover:text-white transition-all"
                  >
                    Filter zurücksetzen
                  </button>
                </>
              ) : (
                <>
                  <h2 className="text-xl font-bold text-gray-700 mb-2">Noch keine Bewertungen</h2>
                  <p className="text-gray-500">
                    Sei der Erste! Nach deiner Fahrt erhältst du automatisch eine Einladung zur Bewertung.
                  </p>
                </>
              )}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold text-gray-900">
                  {total} {total === 1 ? "Bewertung" : "Bewertungen"}
                  {hasActiveFilters && <span className="text-base font-medium text-gray-400"> (gefiltert)</span>}
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                {reviews.map((r) => (
                  <ReviewCard key={r._id} review={r} />
                ))}
              </div>

              {/* Load More */}
              {page < totalPages && (
                <div className="text-center mt-10">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="inline-flex items-center gap-2 px-8 py-3 rounded-full border-2 border-[var(--main-color)] text-[var(--main-color)] font-semibold hover:bg-[var(--main-color)] hover:text-white transition-all duration-300 disabled:opacity-50"
                  >
                    {loadingMore ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                    Mehr Bewertungen laden
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="container-custom max-w-3xl text-center">
          <h2 className="text-3xl font-bold mb-4">Bereit für dein eigenes Rennerlebnis?</h2>
          <p className="text-gray-300 text-lg mb-8">
            Erlebe den Adrenalinkick auf unserer Indoor-Kartbahn und hinterlasse danach deine eigene Bewertung!
          </p>
          <a
            href="/buchen"
            className="inline-flex items-center gap-2 rounded-full bg-[var(--main-color)] px-8 py-4 text-lg font-bold text-white hover:opacity-90 transition-opacity shadow-lg"
          >
            🏁 Jetzt Rennzeit buchen
          </a>
        </div>
      </section>
    </>
  );
}
